// The React half of the page stack: `@bindery/auth-consumer/react`. Kept
// apart from `./browser` so a page that never renders with React does not
// need it installed.

import { isUnauthenticated } from "@bindery/shared/auth/isUnauthenticated";
import { useEffect, useState, useSyncExternalStore } from "react";
import { fetchToken, fetchTokenResult, type TokenResult } from "./browser";

// "unknown" is a real state, not a stand-in for "anonymous": until Convex
// settles the token, a signed-in reader must see a skeleton, not "sign in".
export type AuthState = "unknown" | "authenticated" | "anonymous";

export interface AuthStore {
  readonly getState: () => AuthState;
  readonly subscribe: (listener: () => void) => () => void;
  readonly publish: (isAuthenticated: boolean) => void;
}

/** The slice of `ConvexReactClient` the store wires itself through. */
export interface ConvexAuthClient {
  setAuth(
    fetchToken: () => Promise<string | null>,
    onChange: (isAuthenticated: boolean) => void,
  ): void;
}

export function createConvexAuthStore(): AuthStore {
  let state: AuthState = "unknown";
  const listeners = new Set<() => void>();
  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    publish(isAuthenticated) {
      const next: AuthState = isAuthenticated ? "authenticated" : "anonymous";
      if (next === state) return;
      state = next;
      for (const listener of listeners) listener();
    },
  };
}

// Plain ConvexProvider plus setAuth, not ConvexProviderWithAuth: onChange is
// the only signal, so `convex/react`'s useConvexAuth is unavailable.
export function wireConvexAuth(
  client: ConvexAuthClient,
  store: AuthStore,
  onChange?: (isAuthenticated: boolean) => void,
): void {
  client.setAuth(fetchToken, (isAuthenticated) => {
    store.publish(isAuthenticated);
    onChange?.(isAuthenticated);
  });
}

// The server snapshot is a constant "unknown": a prerender has no cookie, no
// token fetch and no socket, and react-dom's server renderer throws without it.
export function useAuthState(store: AuthStore): AuthState {
  return useSyncExternalStore(store.subscribe, store.getState, () => "unknown");
}

// For a mutation's catch: an unauthenticated refusal means the session died
// under the page, so the store drops to anonymous and the caller shows the
// sign-in prompt instead of the raw error.
export function signedOutBy(error: unknown, store: AuthStore): boolean {
  if (!isUnauthenticated(error)) return false;
  store.publish(false);
  return true;
}

/**
 * The page's session as `/auth/token` reports it. "unreachable" is not
 * "signed-out": a broken auth setting or a down token route must not send the
 * reader to the broker, which would send them straight back, without end.
 */
export type TokenSession =
  | { kind: "loading" }
  | { kind: "signed-out" }
  | { kind: "unreachable" }
  | { kind: "ready"; token: string };

export type TokenSessionStatus = TokenSession["kind"];

export function tokenSessionOf(result: TokenResult): TokenSession {
  switch (result.kind) {
    case "token":
      return { kind: "ready", token: result.token };
    case "signed-out":
      return { kind: "signed-out" };
    case "unreachable":
      return { kind: "unreachable" };
  }
}

/**
 * One token ask per mount. `load` defaults to the `/auth` consumer's
 * `fetchTokenResult`; a caller bound elsewhere passes its own, and it must be
 * stable across renders or each render asks again.
 */
export function useTokenSession(
  load: () => Promise<TokenResult> = fetchTokenResult,
): TokenSession {
  const [session, setSession] = useState<TokenSession>({ kind: "loading" });
  useEffect(() => {
    let cancelled = false;
    void load().then((result) => {
      if (!cancelled) setSession(tokenSessionOf(result));
    });
    return () => {
      cancelled = true;
    };
  }, [load]);
  return session;
}
